import React, { useEffect, useState } from 'react';
import { StaffShell } from '../components/StaffShell';
import { getAdminUsers, getAdminStats, getAdminAuditLogs } from '../lib/api';

const STAT_META = [
  { key: 'total_users', label: 'Total users', icon: 'fa-users', color: '#7C3AED' },
  { key: 'completed_onboarding', label: 'Onboarded', icon: 'fa-user-check', color: '#14B8A6' },
  { key: 'total_checkins', label: 'Check-ins', icon: 'fa-pen-to-square', color: '#F97316' },
  { key: 'total_assessments', label: 'Assessments', icon: 'fa-wand-magic-sparkles', color: '#EC4899' },
];

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('users');

  useEffect(() => {
    getAdminStats().then(setStats).catch(() => {});
    getAdminUsers().then(setUsers).catch(() => {});
    getAdminAuditLogs().then(setLogs).catch(() => {});
  }, []);

  return (
    <StaffShell title="Admin" badge="Operations">
      <h1 className="font-display text-3xl text-white mb-6">Admin dashboard</h1>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {STAT_META.map((s) => (
          <div key={s.key} className="glass-dark rounded-2xl p-4">
            <div className="grid place-items-center w-10 h-10 rounded-xl mb-2" style={{ background: `${s.color}22`, color: s.color }}><i className={`fa-solid ${s.icon}`} /></div>
            <div className="font-display text-3xl text-white">{stats[s.key] ?? 0}</div>
            <div className="text-xs text-white/50">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-4">
        {[{ k: 'users', l: 'Patients & staff' }, { k: 'logs', l: 'Audit logs' }].map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className="rounded-full px-4 py-2 text-sm font-medium transition-base" style={tab === t.k ? { background: 'var(--aura-purple)', color: '#fff' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <div className="glass-dark rounded-2xl overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
            <div className="col-span-4">Name</div><div className="col-span-4">Email</div><div className="col-span-2">Role</div><div className="col-span-2">Stage</div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 px-4 py-3 border-b border-white/5 text-sm sm:items-center">
              <div className="col-span-4 flex items-center justify-between gap-2">
                <span className="text-white font-medium truncate">{u.name}</span>
                <span className="sm:hidden text-[11px] rounded-full px-2 py-0.5 bg-white/10 text-white/80 capitalize shrink-0">{u.role}</span>
              </div>
              <div className="col-span-4 text-white/60 truncate">{u.email}</div>
              <div className="hidden sm:block col-span-2"><span className="text-xs rounded-full px-2.5 py-1 bg-white/10 text-white/80 capitalize">{u.role}</span></div>
              <div className="col-span-2 text-white/60 capitalize"><span className="sm:hidden text-white/40">Stage: </span>{u.menopause_stage}</div>
            </div>
          ))}
          {users.length === 0 && <div className="p-8 text-center text-white/40">No users.</div>}
        </div>
      )}

      {tab === 'logs' && (
        <div className="glass-dark rounded-2xl overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
            <div className="col-span-5">User</div><div className="col-span-4">Action</div><div className="col-span-3 text-right">When</div>
          </div>
          {logs.map((l) => (
            <div key={l.id} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`audit-log-${l.id}`}>
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-white/10 text-white/80 text-xs font-semibold shrink-0">
                  {(l.user_name || 'U').charAt(0)}
                </span>
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{l.user_name}</div>
                  <div className="text-white/40 text-xs truncate">{l.user_email || l.user_role}</div>
                </div>
              </div>
              <div className="col-span-4 flex items-center gap-2 flex-wrap">
                <span className="text-xs rounded-full px-2 py-0.5 bg-white/10 text-white/70 capitalize">{l.user_role}</span>
                <span className="text-[var(--aura-purple-light)] font-medium capitalize">{l.action}</span>
                <span className="text-white/60">{(l.resource_type || '').replace(/_/g, ' ')}</span>
              </div>
              <div className="col-span-3 text-white/40 text-xs sm:text-right">{new Date(l.timestamp).toLocaleString('en-GB')}</div>
            </div>
          ))}
          {logs.length === 0 && <div className="p-8 text-center text-white/40">No audit logs.</div>}
        </div>
      )}
    </StaffShell>
  );
}
